import { reportFailure } from './feedback';
import writeXlsxFile from 'write-excel-file';

type Cell = string | number | null | undefined;

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const UAE = 4 * 3600_000;
/**
 * Dates and date-times become real Excel values (sortable, filterable), shown as on screen:
 * "2026-10-26", ISO timestamps (to UAE time), and the screen formats "26 Oct 2026" / "26 Oct 2026, 03:45 PM".
 */
function asDate(v: string): { value: Date; format: string } | null {
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (m) return { value: new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!)), format: 'dd mmm yyyy' };
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
    const t = Date.parse(v);
    return Number.isNaN(t) ? null : { value: new Date(t + UAE), format: 'dd mmm yyyy hh:mm AM/PM' };
  }
  m = /^(\d{1,2}) ([A-Za-z]{3})[a-z]* (\d{4})(?:, (\d{1,2}):(\d{2}) ?([AP]M))?$/i.exec(v);
  const mon = m ? MONTHS.indexOf(m[2]!.toLowerCase()) : -1;
  if (!m || mon < 0) return null;
  if (!m[4]) return { value: new Date(Date.UTC(+m[3]!, mon, +m[1]!)), format: 'dd mmm yyyy' };
  const h = (+m[4] % 12) + (m[6]!.toUpperCase() === 'PM' ? 12 : 0);
  return { value: new Date(Date.UTC(+m[3]!, mon, +m[1]!, h, +m[5]!)), format: 'dd mmm yyyy hh:mm AM/PM' };
}

const cell = (v: Cell) => {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return { type: Number, value: v };
  const d = asDate(String(v));
  return d ? { type: Date, ...d } : { type: String, value: String(v) };
};

/** Downloads a real .xlsx (numbers stay numbers, header row bold) — opens directly in Excel. */
export async function downloadXlsx(name: string, headers: string[], rows: Cell[][]) {
  const data = [
    headers.map((h) => ({ value: h, fontWeight: 'bold' as const })),
    ...rows.map((r) => r.map(cell)),
  ];
  try {
    await writeXlsxFile(data as never, { fileName: `${name}.xlsx`, columns: headers.map((h) => ({ width: Math.min(Math.max(h.length + 2, 12), 50) })) });
  } catch (e) {
    reportFailure('Export failed', e);
  }
}
